import { DialogContent, DialogContentProps, DialogHeading, DialogHeadingProps } from 'omakase';
import { DialogCore, DialogCoreBaseProps } from 'omakase/Dialog/DialogCore';
import DialogActions from 'omakase/Dialog/DialogActions';
import React, { FunctionComponent } from 'react';
import _ from 'lodash';

type DialogTypeMobile = 'fullScreen' | 'sheet' | 'partial';

interface TockDialogBaseProps {
  /**
   * The id(s) of the element(s) that label the dialog. Or it may be
   * hard-coded to the sentinel value TITLE_TEXT in which case the
   * labelledby attribute will be applied to the title label. Use this
   * option only if headingConfig is defined.
   */
  'aria-labelledby'?: string | typeof TITLE_TEXT;
  /**
   * The id(s) of the element(s) that label the dialog. Or it may be
   * hard-coded to the sentinel value CONTENT_TEXT, in which case the
   * labelledby attribute will be applied to the dialog content.
   */
  'aria-describedby'?: string | typeof CONTENT_TEXT;
  /**
   * Content to be placed in actions (footer) element (existing <div>)
   */
  actionsContent?: React.ReactNode;
}

export type TockDialogProps = TockDialogBaseProps & {
  type: DialogTypeMobile;
} & DialogCoreBaseProps &
  DialogContentProps &
  DialogHeadingProps;

export const TITLE_TEXT = 'TITLE_TEXT';
export const CONTENT_TEXT = 'CONTENT_TEXT';

const TockDialog: FunctionComponent<TockDialogProps> = ({
  children,
  className,
  disablePadding,
  headingConfig,
  actionsContent,
  fullHeight,
  fullWidth = true,
  maxWidth = 'sm',
  onClose,
  open,
  type,
  showShadowWhenScrolled,
  'aria-label': ariaLabel,
  'aria-labelledby': alb,
  'aria-describedby': adb,
  ...props
}) => {
  const headingIdRef = React.useRef<string>(_.uniqueId('heading_'));
  const contentIdRef = React.useRef<string>(_.uniqueId('content_'));

  return (
    <DialogCore
      onClose={onClose}
      entranceMotion="up"
      entranceMotionDesktop="fadeIn"
      variantDesktop="centered"
      fullHeight
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      variant={type}
      open={open}
      aria-label={
        ariaLabel === TITLE_TEXT && headingConfig?.left?.configType === 'title'
          ? headingConfig.left.title
          : ariaLabel === TITLE_TEXT && headingConfig?.center?.configType === 'title'
          ? headingConfig.center.title
          : undefined
      }
      aria-labelledby={alb === TITLE_TEXT ? headingIdRef.current : alb}
      aria-describedby={adb === CONTENT_TEXT ? contentIdRef.current : adb}
      {...props}
    >
      {headingConfig ? (
        <DialogHeading
          headingConfig={headingConfig}
          onClose={onClose}
          id={headingIdRef.current}
          showShadowWhenScrolled={showShadowWhenScrolled}
        />
      ) : null}
      <DialogContent
        className={className}
        disablePadding={disablePadding}
        id={contentIdRef.current}
      >
        {children}
      </DialogContent>
      {actionsContent ? <DialogActions>{actionsContent}</DialogActions> : null}
    </DialogCore>
  );
};

export default TockDialog;
